/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";

import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/modules/auth";
import { useSwal } from "@/hooks/useSwal";
import { useAttendance } from "@/hooks/useAttendance";
import { filterObjectExcluding } from "@/utils/filterObject";
import { preInductionAnesthesia } from "@/utils/initialValues/preInductionAnesthesia";

import { ProfessionalWithRolesSelect } from "@/components/Selects/ProfessionalWithRolesSelect";
import { TagHTMLOrSkeleton } from "@/components/TagHTMLOrSkeleton";
import { FormGroupRadio } from "@/components/Radio/FormGroupRadio";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { SaveButton } from "@/components/Buttons/SaveButton";
import { FormRadio } from "@/components/Radio/FormRadio";
import { Divider } from "@/components/Divider";

import "./styles.scss";

type Props = {
	remainingTime: number;
};

export function PreInductionAnesthesia({ remainingTime }: Props) {
	const [isThePostForm, setIsThePostForm] = useState(true);
	const [isDisabled, setIsDisabled] = useState(false);
	const [skeleton, setSkeleton] = useState(true);
	const completedGetPreAnestheticInduction = useRef(false);
	const { attendance } = useAttendance();
	const { currentAuth } = useAuth();
	const { Toast } = useSwal();
	const { api } = useApi();

	const expertise = attendance.surgicalAttendance?.expertise;
	const isPregnant = expertise?.pregnant;

	const formik = useFormik({
		initialValues: preInductionAnesthesia,
		async onSubmit() {
			try {
				setIsDisabled(true);

				await handleSubmit();

				Toast.fire({
					title: "Pré-indução anestésica salva com sucesso!",
					icon: "success",
				});
			} catch (error) {
				Toast.fire({
					title: "Erro ao salvar pré-indução anestésica!",
					icon: "error",
				});
			} finally {
				setIsDisabled(false);
			}
		},
	});

	const values = formik.values;

	async function handleSubmit() {
		const userProfileName = currentAuth?.user.profiles[0].name;

		if (
			!values.responsible_professional_id &&
			(userProfileName === "Enfermeiro" || userProfileName === "Tec.enfermagem")
		) {
			formik.setFieldValue("responsible_professional_id", currentAuth?.user?.id);
			values.responsible_professional_id = String(currentAuth?.user?.id) || "0";
		}

		const filteredValues = filterObjectExcluding(formik.values, [
			"performed_trichotomy",
			"rapid_hiv_and_syphilis_test_performed",
		]);

		const method = isThePostForm ? "post" : "put";

		await api[method](
			`surgical-center/${attendance.id}/pre-anesthetic-induction`,
			isPregnant ? formik.values : filteredValues
		);

		setIsThePostForm(false);
	}

	async function getPreAnestheticInduction() {
		try {
			const { data } = await api.get(
				`surgical-center/${attendance.id}/pre-anesthetic-induction`
			);

			completedGetPreAnestheticInduction.current = true;
			formik.setValues(data);
			setIsThePostForm(false);
			setSkeleton(false);
		} catch (error) {
			completedGetPreAnestheticInduction.current = true;
			setSkeleton(false);
		}
	}

	useEffect(() => {
		if (attendance.id) {
			getPreAnestheticInduction();
		}
	}, [attendance.id]);

	useEffect(() => {
		if (!remainingTime && attendance.id && completedGetPreAnestheticInduction.current) {
			handleSubmit();
		}
	}, [remainingTime, attendance.id, completedGetPreAnestheticInduction.current]);

	return (
		<>
			<div className="px-5">
				<TagHTMLOrSkeleton
					text="Posto de internação"
					tag="h6"
					className="font-weight-bold mt-3 mb-6"
					skeleton={skeleton}
					skeletonWidth="20%"
				/>

				<TagHTMLOrSkeleton
					text="O paciente confirmou a sua identidade, o local da cirurgia e o procedimento a ser realizado"
					tag="p"
					className="font-weight-bold form-label mb-8"
					skeleton={skeleton}
					skeletonHeight={13}
					containerClassName="d-flex mt-4"
				/>
			</div>

			<Form onSubmit={formik.handleSubmit}>
				<div className="px-5">
					{skeleton ? (
						<SkeletonWrapper containerClassName="d-flex gap-12 mt-4" count={2} inline />
					) : (
						<Row className="mb-4">
							<Col>
								<Form.Check
									label="Identificação"
									id="Identity"
									name="identification"
									onChange={formik.handleChange}
									checked={values.identification}
								/>
							</Col>

							<Col>
								<Form.Check
									label="Jejum"
									id="fasting"
									name="fasting"
									onChange={formik.handleChange}
									checked={values.fasting}
								/>
							</Col>
						</Row>
					)}

					{skeleton ? (
						<SkeletonWrapper containerClassName="d-flex gap-12 mt-4" count={2} inline />
					) : (
						<Row className="mb-4">
							<Col>
								<Form.Check
									label="Local da cirurgia a ser feita"
									id="surgery-location"
									name="surgery_location"
									onChange={formik.handleChange}
									checked={values.surgery_location}
								/>
							</Col>

							<Col>
								<Form.Check
									label="TCLE informado e assinado"
									id="informed-and-signed-terms"
									name="informed_and_signed_terms"
									onChange={formik.handleChange}
									checked={values.informed_and_signed_terms}
								/>
							</Col>
						</Row>
					)}

					{skeleton ? (
						<SkeletonWrapper
							containerClassName="d-flex gap-12 mt-4 mb-6"
							count={2}
							inline
						/>
					) : (
						<Row className="mb-8">
							<Col>
								<Form.Check
									label="Retirada de adornos"
									id="removal-of-adornments"
									name="removal_of_adornments"
									onChange={formik.handleChange}
									checked={values.removal_of_adornments}
								/>
							</Col>

							<Col>
								<Form.Check
									label="Presença de acompanhante"
									id="presence-of-companion"
									name="presence_of_companion"
									onChange={formik.handleChange}
									checked={values.presence_of_companion}
								/>
							</Col>
						</Row>
					)}

					{/* radios */}
					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Reserva de sangue confirmado (Possibilidade de perda
									sanguínea maior que 500ml em adulto ou em >7ml/kg em criança):"
								name="blood_reserve"
								labelClassName="w-60 m-0"
								requestedValue={values.blood_reserve}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio label="Sim" value="1" id="blood-reserve-yes" />
								<FormRadio label="Não" value="0" id="blood-reserve-no" />
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Avaliação pré-anestésica foi realizada?"
								name="pre_anesthetic_evaluation_performed"
								labelClassName="w-60 m-0"
								requestedValue={values.pre_anesthetic_evaluation_performed}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio
									label="Sim"
									value="1"
									id="pre-anesthetic-evaluation-performed-yes"
								/>
								<FormRadio
									label="Não"
									value="0"
									id="pre-anesthetic-evaluation-performed-no"
								/>
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="O paciente tem alguma alergia conhecida?"
								name="allergic_patient"
								labelClassName="w-60 m-0"
								requestedValue={values.allergic_patient}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio label="Sim" value="1" id="allergic-patient-yes" />
								<FormRadio label="Não" value="0" id="allergic-patient-no" />
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Equipe de enfermagem orientou e preparou o paciente?"
								name="patient_oriented_and_prepared"
								labelClassName="w-60 m-0"
								requestedValue={values.patient_oriented_and_prepared}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio
									label="Sim"
									value="1"
									id="patient-oriented-and-prepared-yes"
								/>
								<FormRadio
									label="Não"
									value="0"
									id="patient-oriented-and-prepared-no"
								/>
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Oxímetro de pulso instalado e funcionando?"
								name="pulse_oximeter_installed"
								labelClassName="w-60 m-0"
								requestedValue={values.pulse_oximeter_installed}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio
									label="Sim"
									value="1"
									id="pulse-oximeter-installed-yes"
								/>
								<FormRadio label="Não" value="0" id="pulse-oximeter-installed-no" />
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Exames disponíveis/Imagens"
								name="exams_available"
								labelClassName="w-60 m-0"
								requestedValue={values.exams_available}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio label="Sim" value="1" id="exams-available-yes" />
								<FormRadio label="Não" value="0" id="exams-available-no" />
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Via aérea difícil?"
								name="difficult_airway"
								labelClassName="w-60 m-0"
								requestedValue={values.difficult_airway}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio label="Sim" value="1" id="difficult-airway-yes" />
								<FormRadio label="Não" value="0" id="difficult-airway-no" />
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 py-5">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Monitorização instalada?"
								name="monitoring_installed"
								labelClassName="w-60 m-0"
								requestedValue={values.monitoring_installed}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio label="Sim" value="1" id="monitoring-installed-yes" />
								<FormRadio label="Não" value="0" id="monitoring-installed-no" />
							</FormGroupRadio>
						</Col>
					</div>

					<div className="border-top border-gray-300 pt-5 pb-9">
						<Col
							lg={9}
							md={10}
							sm={9}
							className="d-flex align-items-center justify-content-between"
						>
							<FormGroupRadio
								label="Checagem de equipamentos anestésicos?"
								name="anesthetic_equipment_check"
								labelClassName="w-60 m-0"
								requestedValue={values.anesthetic_equipment_check}
								skeleton={skeleton}
								containerClassName="flex-grow-1"
								onChange={formik.handleChange}
								inline
							>
								<FormRadio
									label="Sim"
									value="1"
									id="anesthetic-equipment-check-yes"
								/>
								<FormRadio
									label="Não"
									value="0"
									id="anesthetic-equipment-check-no"
								/>
							</FormGroupRadio>
						</Col>
					</div>
				</div>

				{isPregnant && completedGetPreAnestheticInduction.current && (
					<>
						<Divider />

						<div className="px-5 mt-7 mb-10">
							<TagHTMLOrSkeleton
								text="Obstetrícia"
								tag="h6"
								className="font-weight-bold mb-8"
								skeletonWidth="20%"
							/>

							<div className="pb-5">
								<Col
									lg={9}
									md={10}
									sm={9}
									className="d-flex align-items-center justify-content-between"
								>
									<FormGroupRadio
										label="Realizado tricotomia?"
										name="performed_trichotomy"
										labelClassName="w-60 m-0"
										requestedValue={values.performed_trichotomy ?? ""}
										onChange={formik.handleChange}
										inline
									>
										<FormRadio
											label="Sim"
											value="1"
											id="performed-trichotomy-yes"
										/>
										<FormRadio
											label="Não"
											value="0"
											id="performed_trichotomy-no"
										/>
									</FormGroupRadio>
								</Col>
							</div>

							<div className="border-top border-gray-300 pt-5">
								<Col
									lg={9}
									md={10}
									sm={9}
									className="d-flex align-items-center justify-content-between"
								>
									<FormGroupRadio
										label="Teste rápido de HIV e Sífilis foi realizado?"
										name="rapid_hiv_and_syphilis_test_performed"
										labelClassName="w-60 m-0"
										requestedValue={
											values.rapid_hiv_and_syphilis_test_performed
										}
										onChange={formik.handleChange}
										inline
									>
										<FormRadio
											label="Sim"
											value="1"
											id="rapid-hiv-and-syphilis-test-performed-yes"
										/>
										<FormRadio
											label="Não"
											value="0"
											id="rapid-hiv-and-syphilis-test-performed-no"
										/>
									</FormGroupRadio>
								</Col>
							</div>
						</div>
					</>
				)}

				<Divider />

				<div className="px-5 mt-7 mb-10">
					<div className="d-flex align-items-center gap-4 mb-8">
						{skeleton ? (
							<SkeletonWrapper
								containerClassName="d-flex flex-grow-1 gap-8"
								width="25%"
								count={2}
								inline
							/>
						) : (
							<>
								<h6 className="font-weight-bold m-0">Profissional responsável:</h6>

								<Col lg={4} md={6} xs={5}>
									<ProfessionalWithRolesSelect
										menuPlacement="auto"
										roles={["Técnico de Enfermagem", "Enfermeiro"]}
										professionalId={values.responsible_professional_id}
										onChange={(option) =>
											formik.setFieldValue(
												"responsible_professional_id",
												(option as ReactSelectOption).value
											)
										}
										isSolid
									/>
								</Col>
							</>
						)}
					</div>

					<div className="d-flex justify-content-end">
						<SaveButton type="submit" disabled={isDisabled} skeleton={skeleton} />
					</div>
				</div>
			</Form>
		</>
	);
}
